import { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Button, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useGetSystemSettingsQuery } from '@/store/api/settingsApi';
import { selectStoreAndOutlet } from '@/store/slices/storeSlice';

export const TimezoneMismatchWarning = () => {
  const { t } = useTranslation('common');
  const { storeId, outletId } = useSelector(selectStoreAndOutlet);
  const [dismissed, setDismissed] = useState(false);

  const { data } = useGetSystemSettingsQuery(
    { storeId: storeId || '', outletId: outletId || undefined },
    { skip: !storeId }
  );

  const configuredTz = data?.data?.timezone;
  const browserTz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const dismissalKey = useMemo(
    () =>
      storeId && configuredTz
        ? `tz_mismatch_dismissed_${storeId}_${configuredTz}_${browserTz}`
        : null,
    [storeId, configuredTz, browserTz]
  );

  const hasMismatch = Boolean(configuredTz && configuredTz !== browserTz);

  useEffect(() => {
    if (!dismissalKey) {
      setDismissed(false);
      return;
    }
    setDismissed(localStorage.getItem(dismissalKey) === 'true');
  }, [dismissalKey]);

  const handleDismiss = () => {
    if (dismissalKey) {
      localStorage.setItem(dismissalKey, 'true');
    }
    setDismissed(true);
  };

  if (!hasMismatch || dismissed) {
    return null;
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: 'warning.dark',
        color: 'white',
        p: { xs: 2, sm: 3 },
        borderRadius: 0,
      }}
    >
      <Box
        sx={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {t('timezoneMismatch.title')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {t('timezoneMismatch.message', { configuredTz, browserTz })}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <Button
            variant="contained"
            href="/settings"
            sx={{
              flex: { xs: 1, md: 'none' },
              backgroundColor: 'white',
              color: 'warning.dark',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
              },
            }}
          >
            {t('timezoneMismatch.goToSettings')}
          </Button>
          <IconButton
            aria-label={t('timezoneMismatch.dismiss')}
            onClick={handleDismiss}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};
